import scales from '../data/scales.json';
import { getKeyPitchClass } from './keyDistance';

const { FLAT_SCALE, SHARP_SCALE, PREFERRED_FLAT_SCALES } = scales;

// Export the scales for use in other components
export { FLAT_SCALE, SHARP_SCALE };

// Chord quality patterns for major and minor scales
const MAJOR_SCALE_PATTERN = ['', 'm', 'm', '', '', 'm', 'dim'];
const MINOR_SCALE_PATTERN = ['m', 'dim', '', 'm', 'm', '', ''];

// Roman numeral notation for reference
const MAJOR_ROMAN = ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'];
const MINOR_ROMAN = ['i', 'ii°', 'III', 'iv', 'v', 'VI', 'VII'];

/**
 * Get the chromatic scale (flat or sharp) based on key preference
 * @param {string} key - The key (e.g., 'C', 'Fm', 'F#/Gb')
 * @returns {Array} - The appropriate chromatic scale
 */
export function getChromaticScale(key) {
  // Extract base key without modifiers
  const baseKey = key.replace(/m$/, ''); // Remove 'm' for minor

  // Check if it's a preferred flat scale
  if (PREFERRED_FLAT_SCALES.includes(key) || PREFERRED_FLAT_SCALES.includes(baseKey)) {
    return FLAT_SCALE;
  }

  // Check if key contains flat notation
  if (baseKey.includes('b') || baseKey.includes('/')) {
    return FLAT_SCALE;
  }

  return SHARP_SCALE;
}

/**
 * Get the root note from a key string
 * @param {string} key - The key (e.g., 'C', 'Fm', 'F#/Gb')
 * @returns {string} - The root note
 */
export function getRootNote(key) {
  // Handle compound keys like 'F#/Gb'
  if (key.includes('/')) {
    const scale = getChromaticScale(key);
    // Use flat version if using flat scale, sharp if using sharp scale
    const [sharp, flat] = key.split('/');
    const sharpNote = sharp.replace(/m$/, '');
    const flatNote = flat.replace(/m$/, '');
    return scale === FLAT_SCALE ? flatNote : sharpNote;
  }

  // Remove 'm' suffix for minor keys
  return key.replace(/m$/, ''); // Only remove trailing 'm' for minor indicators
}

/**
 * Check if a key is minor
 * @param {string} key - The key (e.g., 'C', 'Fm', 'F#/Gb')
 * @returns {boolean} - True if minor key
 */
export function isMinorKey(key) {
  return key.includes('m');
}

// The name each black-key tonic goes by, by pitch class. An array is the pair the reader's
// preference picks from — the keys musicians genuinely write both ways; the rest have one usual name.
const MAJOR_KEY_NAMES = { 1: ['C#', 'Db'], 3: 'Eb', 6: ['F#', 'Gb'], 8: 'Ab', 10: 'Bb' };
const MINOR_KEY_NAMES = { 1: 'C#m', 3: ['D#m', 'Ebm'], 6: 'F#m', 8: 'G#m', 10: 'Bbm' };

/**
 * A key under the name it is usually written with — 'A#/Bb' is Bb, 'G#m/Abm' is G#m. Only C#/Db,
 * F#/Gb and D#m/Ebm are left to the reader's preference. A natural key comes back as it is.
 *
 * @param {string} key - The key in any spelling the app stores (e.g. 'C#', 'Db', 'F#/Gb')
 * @param {boolean} preferSharps - Picks the sharp name for the keys written both ways
 * @returns {string} - The key as it should be shown
 */
export function normalizeKeyDisplay(key, preferSharps = true) {
  const pitchClass = getKeyPitchClass(key);
  if (pitchClass === null) return key;

  const name =(isMinorKey(key) ? MINOR_KEY_NAMES : MAJOR_KEY_NAMES)[pitchClass];

  if (!name) return key;

  return Array.isArray(name) ? name[preferSharps ? 0 : 1] : name;
}

/**
 * Get the note at a specific interval from the root
 * @param {string} rootNote - The root note
 * @param {number} semitones - Number of semitones from root
 * @param {Array} scale - The chromatic scale to use
 * @returns {string} - The note at the interval
 */
export function getNoteAtInterval(rootNote, semitones, scale) {
  const rootIndex = scale.indexOf(rootNote);
  if (rootIndex === -1) return null;

  const targetIndex = (rootIndex + semitones) % 12;
  return scale[targetIndex];
}

/**
 * Generate the 7 diatonic chords for a given key
 * @param {string} key - The key (e.g., 'C', 'Fm', 'F#/Gb', 'C#m/Dbm')
 * @returns {Array} - Array of chord objects with name and roman numeral
 */
export function getDiatonicChords(key) {
  if (!key || typeof key !== 'string') return [];

  try {
    const rootNote = getRootNote(key);
    const isMinor = isMinorKey(key);
    const scale = getChromaticScale(key);

    // Major scale intervals (in semitones): 0, 2, 4, 5, 7, 9, 11
    // Minor scale intervals (in semitones): 0, 2, 3, 5, 7, 8, 10
    const intervals = isMinor
      ? [0, 2, 3, 5, 7, 8, 10] // Natural minor scale
      : [0, 2, 4, 5, 7, 9, 11]; // Major scale

    const chordPattern = isMinor ? MINOR_SCALE_PATTERN : MAJOR_SCALE_PATTERN;
    const romanPattern = isMinor ? MINOR_ROMAN : MAJOR_ROMAN;

    const chords = [];

    for (let i = 0; i < 7; i++) {
      const chordRoot = getNoteAtInterval(rootNote, intervals[i], scale);
      if (chordRoot) {
        const chordQuality = chordPattern[i];
        const chordName = chordRoot + chordQuality;
        const roman = romanPattern[i];

        chords.push({
          name: chordName,
          roman: roman,
          degree: i + 1,
        });
      }
    }

    return chords;
  } catch (error) {
    return [];
  }
}

/**
 * Scale degrees as the key's chords — [1, 5, 6, 4] in C is C G Am F.
 * @param {string} key - The key (e.g., 'C', 'Fm', 'F#/Gb')
 * @param {number[]} degrees - 1 to 7
 * @returns {string[]} - Empty for a key that can't be read
 */
export function getProgressionChords(key, degrees) {
  const chords = getDiatonicChords(key);

  return chords.length === 7 ? degrees.map((degree) => chords[degree - 1].name) : [];
}

/**
 * Classify a chord name as major / minor / diminished — used to colour chord buttons.
 * @param {string} chordName - The chord (e.g., 'C', 'Am', 'Bdim')
 * @returns {string} - 'major' | 'minor' | 'diminished'
 */
export function getChordQuality(chordName) {
  if (!chordName) return 'major';
  if (chordName.includes('°') || chordName.includes('dim')) return 'diminished';
  if (chordName.includes('m') && !chordName.includes('maj')) return 'minor';
  return 'major';
}

/**
 * Insert chord at cursor position in textarea
 * @param {string} chordName - The chord to insert
 * @param {Function} setInputToState - Function to update form state
 */
export function insertChordAtCursor(chordName, setInputToState) {
  const textArea = document.getElementById('currentLyrics');
  if (!textArea) return;

  const selectionStart = textArea.selectionStart;
  const selectionEnd = textArea.selectionEnd;
  const currentValue = textArea.value;

  // Insert chord at cursor position
  const beforeCursor = currentValue.substring(0, selectionStart);
  const afterCursor = currentValue.substring(selectionEnd);
  const newValue = beforeCursor + chordName + afterCursor;

  // Update form state
  setInputToState({
    currentTarget: {
      name: 'currentLyrics',
      value: newValue,
    },
  });

  // Set cursor position after the inserted chord
  setTimeout(() => {
    textArea.focus();
    const newCursorPosition = selectionStart + chordName.length;
    textArea.setSelectionRange(newCursorPosition, newCursorPosition);
  }, 0);
}
